import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Lock, Copy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const cls = "w-full rounded-md border border-white/10 bg-[#0A0D14] px-3 py-2 font-sans text-sm text-[#F1F5F9] focus:border-gold focus:outline-none";
const TABS = [["positions", "Departments & Positions"], ["documents", "Document Options"], ["questions", "Screening Questions"]];
const err = (e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed");

function PositionDialog({ pos, departments, onClose, onSaved }) {
  const [f, setF] = useState({ department: pos.department || "", title: pos.title || "", responsibilities: (pos.responsibilities || []).join("\n"), requirements: (pos.requirements || []).join("\n") });
  const save = async () => {
    const body = { ...f, responsibilities: f.responsibilities.split("\n"), requirements: f.requirements.split("\n") };
    try {
      if (pos.id) await api.put(`/admin/job-catalog/${pos.id}`, body); else await api.post("/admin/job-catalog", body);
      toast.success("Position saved"); onSaved();
    } catch (e) { err(e); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-[#121722] text-[#F1F5F9]">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{pos.id ? "Edit position" : pos.title ? "Copy position" : "Add position"}</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">Type a new department name or pick an existing one.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <input list="dept-list" placeholder="Department" value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} className={cls} data-testid="cat-department" />
          <datalist id="dept-list">{departments.map((d) => <option key={d} value={d} />)}</datalist>
          <input placeholder="Position title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={cls} data-testid="cat-title" />
          <label className="block font-sans text-xs text-[#94A3B8]">Responsibilities (one per line)</label>
          <textarea rows={5} value={f.responsibilities} onChange={(e) => setF({ ...f, responsibilities: e.target.value })} className={cls} data-testid="cat-responsibilities" />
          <label className="block font-sans text-xs text-[#94A3B8]">Requirements (one per line)</label>
          <textarea rows={4} value={f.requirements} onChange={(e) => setF({ ...f, requirements: e.target.value })} className={cls} data-testid="cat-requirements" />
          <button onClick={save} data-testid="cat-save" className="w-full rounded-full bg-gold px-6 py-3 font-sans text-xs font-semibold uppercase tracking-widest text-[#0A0D14]">Save position</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OptionList({ items, labelKey, addPath, delPath, placeholder, onChange, testid }) {
  const [val, setVal] = useState("");
  const add = async () => {
    if (val.trim().length < 2) return;
    try { await api.post(addPath, { label: val.trim() }); setVal(""); toast.success("Added"); onChange(); } catch (e) { err(e); }
  };
  const del = async (it) => {
    if (!window.confirm(`Remove "${it[labelKey]}" from the options? Existing jobs keep it.`)) return;
    try { await api.delete(`${delPath}/${it.id}`); toast.success("Removed"); onChange(); } catch (e) { err(e); }
  };
  return (
    <div>
      <div className="flex gap-2">
        <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={placeholder} className={cls} data-testid={`${testid}-input`} />
        <button onClick={add} data-testid={`${testid}-add`} className="flex shrink-0 items-center gap-1.5 rounded-full bg-gold px-5 py-2 font-sans text-xs font-semibold uppercase tracking-widest text-[#0A0D14]"><Plus size={14} /> Add</button>
      </div>
      <div className="mt-4 overflow-hidden rounded-lg border border-white/5">
        {items.map((it) => (
          <div key={it.id} data-testid={`${testid}-row-${it.id}`} className="flex items-center justify-between border-t border-white/5 bg-[#121722] px-5 py-3 first:border-t-0">
            <span className="font-sans text-sm">{it[labelKey]} {it.mandatory && <span className="ml-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-gold"><Lock size={10} /> Mandatory</span>}</span>
            {!it.mandatory && <button onClick={() => del(it)} data-testid={`${testid}-del-${it.id}`} className="rounded-md border border-white/10 p-2 text-[#94A3B8] hover:border-rose-500 hover:text-rose-400"><Trash2 size={14} /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminJobCatalog() {
  const [data, setData] = useState({ positions: [], documents: [], questions: [] });
  const [tab, setTab] = useState("positions");
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/job-catalog").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const departments = useMemo(() => [...new Set(data.positions.map((p) => p.department))], [data]);

  const delPos = async (p) => {
    if (!window.confirm(`Delete ${p.title} from the catalog? Existing job posts are not affected.`)) return;
    try { await api.delete(`/admin/job-catalog/${p.id}`); toast.success("Deleted"); load(); } catch (e) { err(e); }
  };

  return (
    <div>
      <p className="eyebrow">Recruitment</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <h1 className="font-serif text-3xl tracking-tight">Job Catalog</h1>
        {tab === "positions" && <button onClick={() => setEdit({})} data-testid="cat-add-position" className="ml-auto flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-sans text-xs font-semibold uppercase tracking-widest text-[#0A0D14]"><Plus size={15} /> Add position</button>}
      </div>
      <p className="mt-2 font-sans text-sm text-[#94A3B8]">Department → Position → Responsibilities → Requirements used when posting jobs.</p>
      <div className="mt-6 flex flex-wrap gap-2">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`cat-tab-${k}`} className={`rounded-full border px-4 py-1.5 font-sans text-xs uppercase tracking-widest ${tab === k ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-[#94A3B8]"}`}>{l}</button>)}
      </div>

      {tab === "positions" && (
        <div className="mt-6 space-y-6">
          {departments.map((d) => (
            <section key={d}>
              <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-gold">{d} · {data.positions.filter((p) => p.department === d).length} positions</p>
              <div className="overflow-hidden rounded-lg border border-white/5">
                {data.positions.filter((p) => p.department === d).map((p) => (
                  <div key={p.id} data-testid={`cat-row-${p.id}`} className="flex flex-wrap items-start gap-4 border-t border-white/5 bg-[#121722] px-5 py-4 first:border-t-0">
                    <div className="min-w-[240px] flex-1">
                      <p className="font-serif text-lg">{p.title}</p>
                      <p className="mt-1 font-sans text-xs text-[#94A3B8]">{p.responsibilities.length} responsibilities · {p.requirements.length} requirements</p>
                      <p className="mt-1 line-clamp-1 font-sans text-xs text-[#64748B]">{p.responsibilities.join(" · ")}</p>
                    </div>
                    <button onClick={() => setEdit({ ...p, id: undefined, title: `${p.title} (Copy)` })} data-testid={`cat-copy-${p.id}`} className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 font-sans text-xs text-[#94A3B8] hover:border-gold hover:text-gold"><Copy size={13} /> Copy</button>
                    <button onClick={() => setEdit(p)} data-testid={`cat-edit-${p.id}`} className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 font-sans text-xs text-[#94A3B8] hover:border-gold hover:text-gold"><Pencil size={13} /> Edit</button>
                    <button onClick={() => delPos(p)} data-testid={`cat-delete-${p.id}`} className="rounded-md border border-white/10 p-2 text-[#94A3B8] hover:border-rose-500 hover:text-rose-400"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
      {tab === "documents" && <div className="mt-6"><OptionList items={data.documents} labelKey="label" addPath="/admin/doc-options" delPath="/admin/doc-options" placeholder="New document option, e.g. Covid Vaccination Certificate" onChange={load} testid="docopt" /></div>}
      {tab === "questions" && <div className="mt-6"><OptionList items={data.questions} labelKey="question" addPath="/admin/question-options" delPath="/admin/question-options" placeholder="New Yes/No screening question" onChange={load} testid="qopt" /></div>}

      {edit && <PositionDialog pos={edit} departments={departments} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
